import '../../../themes/global.scss';

import { motion } from 'framer-motion';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ClipLoader } from 'react-spinners';

import { BackButton, Box, CardSpinner, Column, Row, Text } from '~/commonComponents';
import { FETCH_INVOICE_LIST, fetchInvoiceList } from '../../../actions';
import { createLoadingSelector } from '../../../api/selectors';
import { getShouldShowConsolidatedInvoices } from '../../../growthbook';
import history from '../../../history';
import { currencyFormat } from '../../../hooks/currencyFormat';
import { Colors } from '../../../themes/colors';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { EnforcePaymentInvoiceV2 } from '~/constants/ff.posthog';
import { PaymentInvoiceTable } from './components/InvoiceTable';

const LIMIT_VALUE = 25;

const normalText = {
  fontFamily: 'Nunito',
  fontSize: 16,
  color: Colors.neutral_600,
  textTransform: 'capitalize',
  padding: 10,
  textAlign: 'center',
};

const titleText = {
  fontFamily: 'Nunito',
  fontWeight: 'bold',
  fontSize: '18px',
  color: Colors.primary_500,
  textTransform: 'capitalize',
  padding: 10,
  textAlign: 'center',
};

export default function InvoiceList() {
  const dispatch = useDispatch();
  const myRef = useRef(null);

  const loadingSelector = createLoadingSelector([FETCH_INVOICE_LIST]);
  const isLoading = useSelector((state) => loadingSelector(state));

  const isEnforcePaymentInvoiceV2 = useFeatureFlag(EnforcePaymentInvoiceV2);

  const {
    invoices,
    isFinalPageInvoices,
    isActiveRequestInvoices,
    receiptReport,
    invoicesPagination,
    totalUnpaid,
  } = useSelector((state) => state.job);

  const cvsDownload = useRef(null);
  const showConsolidatedInvoices = getShouldShowConsolidatedInvoices();
  const gridColumns = showConsolidatedInvoices
    ? '1fr 2fr 1fr 1.25fr 1.25fr 1fr'
    : '1fr 2fr 1fr 1.25fr 1.25fr';

  useEffect(() => {
    dispatch(fetchInvoiceList({ page: 1, limit: LIMIT_VALUE }));
  }, [dispatch]);

  const handleScrollReceipts = () => {
    const userScrollHeight = window.innerHeight + window.scrollY + 1;
    const windowBottomHeight = document.documentElement.offsetHeight;

    if (userScrollHeight > windowBottomHeight && !isActiveRequestInvoices && !isFinalPageInvoices) {
      dispatch(fetchInvoiceList({ page: invoicesPagination?.page + 1, limit: LIMIT_VALUE }));
    }
  };

  useEffect(() => {
    if (cvsDownload.current?.link && receiptReport !== '') {
      cvsDownload.current.link.click();
    }
  }, [receiptReport]);

  if (isLoading && invoices.length === 0) {
    return <CardSpinner />;
  }

  return (
    <div className="component_container">
      <div
        className="floating_container"
        style={{
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: 40,
        }}
      >
        {isEnforcePaymentInvoiceV2 ? (
          <Row position="relative" alignItems="center" justifyContent="center" width="100%">
            <Box position="absolute" left={0}>
              <BackButton />
            </Box>
            <Text bold fontSize={32} textAlign="center">
              Invoices
            </Text>
            <Column position="absolute" right={0}>
              <Text bold textAlign="right">
                Unpaid Invoices - {totalUnpaid.count}
              </Text>
              <Text bold textAlign="right">
                Total of Unpaid Invoices - ${currencyFormat(totalUnpaid.sum)}
              </Text>
            </Column>
          </Row>
        ) : (
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              marginBottom: 20,
              overflowY: 'auto',
            }}
          >
            <Row justifyContent="space-between" marginBottom={20}>
              <BackButton />

              <Column>
                <Text bold textAlign="right">
                  Unpaid Invoices - {totalUnpaid.count}
                </Text>
                <Text bold textAlign="right">
                  Total of Unpaid Invoices - ${currencyFormat(totalUnpaid.sum)}
                </Text>
              </Column>
            </Row>

            <h1 className="global_font f-dark" style={{ marginLeft: 30, textAlign: 'center' }}>
              Invoices
            </h1>

            {invoices.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginLeft: 30,
                  marginTop: 20,
                }}
              >
                <span
                  style={{
                    fontSize: 16,
                    color: '#546e7a',
                    textAlign: 'center',
                  }}
                >
                  There are no invoices.
                </span>
              </div>
            ) : (
              <div
                ref={myRef}
                onScroll={handleScrollReceipts}
                style={{ overflowY: 'scroll', maxHeight: 600 }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: gridColumns,
                    gap: 0,
                    margin: 10,
                  }}
                >
                  <span style={titleText}>Shift</span>
                  <span style={titleText}>Office</span>
                  <span style={titleText}>Amount</span>
                  <span style={titleText}>Shift Date</span>
                  <span style={titleText}>Invoice date</span>
                  {showConsolidatedInvoices && <span style={titleText}>Invoice #</span>}
                </div>
                <div style={{ height: 1, backgroundColor: '#F6F6F6' }} />
                {invoices.map((invoice, index) => (
                  <motion.div
                    whileHover={{
                      boxShadow:
                        '0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
                      transition: { duration: 0.3 },
                    }}
                    key={invoice.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: gridColumns,
                      gap: 0,
                      margin: 10,
                      backgroundColor: index % 2 === 0 ? '#F6F6F6' : '#FAFAFA',
                      borderRadius: '30px',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'Nunito',
                        fontSize: 16,
                        color: Colors.neutral_600,
                        textTransform: 'capitalize',
                        padding: 10,
                        textAlign: 'center',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        history.push(`/dashboard/job/${invoice?.job_id}`, {
                          job_id: invoice?.job_id,
                          jobId: invoice?.job_id,
                        })
                      }
                    >
                      View Shift
                    </span>

                    <span style={normalText}>{invoice.office_name}</span>
                    <span
                      style={{
                        fontFamily: 'Nunito',
                        fontSize: 16,
                        color: Colors.neutral_600,
                        textTransform: 'capitalize',
                        padding: 10,
                        fontWeight: 'bold',
                        textAlign: 'center',
                      }}
                    >
                      {invoice.type === 'credit'
                        ? `- $${currencyFormat(invoice.amount)}`
                        : `$${currencyFormat(invoice.amount)}`}
                    </span>
                    <span style={normalText}>
                      {moment(invoice.jobDate).format('ddd, MMM DD, YYYY')}
                    </span>

                    <span style={normalText}>
                      {`${moment(invoice.date).format('MM-DD-YYYY HH:mm')}`}
                    </span>

                    {showConsolidatedInvoices && (
                      <a
                        style={{
                          fontFamily: 'Nunito',
                          fontSize: 15,
                          color: invoice.invoice_url ? Colors.secondary_500 : Colors.neutral_600,
                          textTransform: 'capitalize',
                          padding: 10,
                          textAlign: 'center',
                          textDecoration: invoice.invoice_url ? 'underline' : 'none',
                        }}
                        href={invoice.invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {invoice.invoice_number || '-'}
                      </a>
                    )}
                  </motion.div>
                ))}
                {!isFinalPageInvoices && isActiveRequestInvoices ? (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <ClipLoader size={50} color={Colors.primary_500} loading />
                  </div>
                ) : (
                  <div />
                )}
              </div>
            )}
          </div>
        )}

        {isEnforcePaymentInvoiceV2 && (
          <PaymentInvoiceTable ref={myRef} onScroll={handleScrollReceipts} />
        )}
      </div>
    </div>
  );
}
